
import { useState } from 'react';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/public/form/submit/rodogael-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '100px 100px'
          }}></div>
        </div>

        {/* Floating Geometric Shapes */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-60 w-48 h-48 bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 rounded-3xl blur-2xl rotate-45"></div>
        <div className="absolute bottom-40 right-40 w-56 h-56 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>

        {/* Navigation */}
        <nav className="relative z-10 px-24 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="https://public.readdy.ai/ai/img_res/f15af6f9-9ded-4789-84b1-4877644e6ded.png" alt="RODOGAEL LTDA Logo" className="h-12 w-auto" />
              <span className="text-white text-xl font-light tracking-widest">RODOGAEL LTDA</span>
            </div>
            <div className="flex gap-12">
              <button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-white transition-colors text-sm tracking-wide cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-white transition-colors text-sm tracking-wide cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-300 hover:text-white transition-colors text-sm tracking-wide cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-300 hover:text-white transition-colors text-sm tracking-wide cursor-pointer whitespace-nowrap">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-white transition-colors text-sm tracking-wide cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)]">
          <div className="text-center max-w-5xl px-8">
            <h1 className="text-7xl font-bold text-white mb-8 leading-tight">
              <span className="block">Modern Applications</span>
              <span className="block ml-16">for the iOS</span>
              <span className="block ml-32">Platform</span>
            </h1>
            <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
              A team of experts developing innovative solutions that enhance user experience and improve the security of mobile devices
            </p>
          </div>
        </div>

        {/* Bottom Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 gap-20">
            {/* Left Column */}
            <div>
              <span className="text-xs text-gray-500 tracking-[0.2em] uppercase font-medium">WHO WE ARE</span>
              <h2 className="text-6xl font-bold text-black mt-6 leading-tight font-['Playfair_Display',serif]">
                <span className="block">A team of</span>
                <span className="block">experts</span>
                <span className="block">developing</span>
                <span className="block">modern applications</span>
              </h2>
              <div className="mt-20 flex flex-wrap gap-3">
                <span className="px-6 py-3 border-2 border-gray-300 rounded-full text-sm text-gray-700 whitespace-nowrap">iOS Development</span>
                <span className="px-6 py-3 border-2 border-gray-300 rounded-full text-sm text-gray-700 whitespace-nowrap">Security Focus</span>
                <span className="px-6 py-3 border-2 border-gray-300 rounded-full text-sm text-gray-700 whitespace-nowrap">User Experience</span>
                <span className="px-6 py-3 border-2 border-gray-300 rounded-full text-sm text-gray-700 whitespace-nowrap">Innovation</span>
                <span className="px-6 py-3 border-2 border-gray-300 rounded-full text-sm text-gray-700 whitespace-nowrap">Quality Assurance</span>
              </div>
            </div>

            {/* Right Column */}
            <div className="flex flex-col justify-center">
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                RODOGAEL LTDA is a dedicated team of iOS development specialists committed to creating cutting-edge mobile applications. Our expertise spans across multiple domains, ensuring that every product we deliver meets the highest standards of quality and innovation.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We focus on developing applications that not only meet user needs but exceed expectations. Our products are designed with a deep understanding of mobile security, performance optimization, and intuitive user interfaces.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With years of experience in the iOS ecosystem, we bring technical excellence and creative solutions to every project. Our mission is to enhance the mobile experience for users worldwide through innovative and secure applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-36 px-24 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            <h2 className="text-6xl font-bold text-black mb-6">Our Products</h2>
            <p className="text-gray-600 text-base max-w-xl mx-auto">
              Innovative solutions designed to enhance your mobile experience and protect your digital life
            </p>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 gap-16" data-product-shop>
            {/* VPN Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mb-8 mx-auto">
                <i className="ri-shield-keyhole-line text-white text-5xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-black mb-4 text-left">VPN</h3>
              <p className="text-gray-600 text-base leading-relaxed text-left">
                Our VPN application provides robust security and privacy protection for your iOS device. With military-grade encryption, fast connection speeds, and servers worldwide, you can browse the internet safely and access content from anywhere. The intuitive interface makes it easy to connect with just one tap, while advanced features give you complete control over your online privacy.
              </p>
            </div>

            {/* Cleaner Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-center w-32 h-32 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mb-8 mx-auto">
                <i className="ri-sparkling-line text-white text-5xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-black mb-4 text-left">Cleaner</h3>
              <p className="text-gray-600 text-base leading-relaxed text-left">
                Keep your iOS device running at peak performance with our Cleaner application. It intelligently identifies and removes unnecessary files, optimizes storage space, and helps manage your photos and videos. With smart algorithms and a user-friendly interface, maintaining your device has never been easier. Free up space and enjoy a faster, more responsive mobile experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-40 px-24 bg-[#1A1A1A]">
        <div className="max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="mb-24">
            <span className="inline-block px-5 py-2 border border-white/30 rounded-full text-white text-xs tracking-[0.2em] uppercase mb-12">FEATURES</span>
            <h2 className="text-7xl font-bold text-white leading-tight max-w-4xl">
              Enhance your mobile experience
            </h2>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-4 gap-10">
            {/* Security */}
            <div className="bg-[#2A2A2A] rounded-2xl p-12 h-48 flex items-center gap-6 hover:bg-[#333333] transition-colors">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-4xl"></i>
              </div>
              <span className="text-white text-lg font-medium">Advanced Security</span>
            </div>

            {/* Performance */}
            <div className="bg-[#2A2A2A] rounded-2xl p-12 h-48 flex items-center gap-6 hover:bg-[#333333] transition-colors">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-speed-line text-white text-4xl"></i>
              </div>
              <span className="text-white text-lg font-medium">High Performance</span>
            </div>

            {/* Interface */}
            <div className="bg-[#2A2A2A] rounded-2xl p-12 h-48 flex items-center gap-6 hover:bg-[#333333] transition-colors">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-layout-line text-white text-4xl"></i>
              </div>
              <span className="text-white text-lg font-medium">Intuitive Interface</span>
            </div>

            {/* Cloud Sync */}
            <div className="bg-[#2A2A2A] rounded-2xl p-12 h-48 flex items-center gap-6 hover:bg-[#333333] transition-colors">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-cloud-line text-white text-4xl"></i>
              </div>
              <span className="text-white text-lg font-medium">Cloud Integration</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="px-6 pb-6 bg-white">
        <div className="bg-[#0F0F0F] rounded-t-[32px] px-24 py-20 relative overflow-hidden">
          {/* Background Watermark */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[180px] font-bold text-[#1A1A1A] opacity-8 whitespace-nowrap pointer-events-none">
            RODOGAEL
          </div>

          <div className="relative z-10 grid grid-cols-12 gap-12">
            {/* Company Information */}
            <div className="col-span-3">
              <h3 className="text-white text-3xl font-bold mb-16">RODOGAEL LTDA</h3>
              <div className="text-[#999999] text-sm leading-relaxed space-y-1">
                <p>Rua JOAO WESSLER 169</p>
                <p>SAO LUDGERO</p>
                <p>88730-000</p>
                <p>Brazil</p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-span-2">
              <h4 className="text-white text-lg font-medium mb-8">Navigation</h4>
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('home')} className="text-[#AAAAAA] text-sm text-left hover:text-white transition-colors cursor-pointer whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-[#AAAAAA] text-sm text-left hover:text-white transition-colors cursor-pointer whitespace-nowrap">About</button>
                <button onClick={() => scrollToSection('products')} className="text-[#AAAAAA] text-sm text-left hover:text-white transition-colors cursor-pointer whitespace-nowrap">Products</button>
                <button onClick={() => scrollToSection('features')} className="text-[#AAAAAA] text-sm text-left hover:text-white transition-colors cursor-pointer whitespace-nowrap">Features</button>
                <button onClick={() => scrollToSection('contact')} className="text-[#AAAAAA] text-sm text-left hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              </div>
            </div>

            {/* Connect */}
            <div className="col-span-3">
              <h4 className="text-white text-lg font-medium mb-8">Connect</h4>
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-[#AAAAAA] text-base"></i>
                  <a href="mailto:support@rodogaelltda.com" className="text-[#AAAAAA] text-sm hover:text-white transition-colors cursor-pointer">support@rodogaelltda.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-[#AAAAAA] text-base"></i>
                  <a href="tel:+14244013252" className="text-[#AAAAAA] text-sm hover:text-white transition-colors cursor-pointer">+1 4244013252</a>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <div className="col-span-4">
              <p className="text-white text-base mb-7 leading-relaxed">
                Stay updated with our<br />latest releases
              </p>
              <form id="newsletter-form" onSubmit={handleNewsletterSubmit} data-readdy-form className="flex items-center gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 h-12 px-5 bg-[#333333] border border-white/10 rounded-full text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-white/30 transition-colors"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line text-black text-xl"></i>
                </button>
              </form>
              {submitMessage && (
                <p className={`mt-3 text-sm ${submitMessage.includes('Thank you') ? 'text-green-400' : 'text-red-400'}`}>
                  {submitMessage}
                </p>
              )}
            </div>
          </div>

          {/* Copyright */}
          <div className="relative z-10 mt-20 pt-8 border-t border-white/10 flex items-center justify-between">
            <p className="text-[#666666] text-xs">
              © 2025 RODOGAEL LTDA. All rights reserved.
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#666666] text-xs hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
